import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import ProfileHeader from '../components/ProfileHeader';
import { db } from '../firebase';
import { getRequesterBookings } from '../utils/booking';
import {
  buildPackageSelectionView,
  getBestNextLessonForDisplay,
  getPackageSelectionLink,
  getRecommendedPackageForClient,
} from '../utils/clientFacingExperience';
import { canViewStudentPackageSelection } from '../utils/platformAccess';
import { getUserActivePackages, getOrganizationServicePackages } from '../utils/servicePackages';
import { getOrganizationServices } from '../utils/services';

function StudentPackageSelectionPage() {
  const { currentUser, userData } = useAuth() || {};
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [services, setServices] = useState([]);
  const [servicePackages, setServicePackages] = useState([]);

  const searchParams = useMemo(() => new URLSearchParams(window.location.search), []);
  const requestedServiceId = searchParams.get('serviceId') || '';
  const requestedPackageId = searchParams.get('packageId') || '';

  useEffect(() => {
    const loadSelection = async () => {
      if (!currentUser?.uid || !userData?.orgId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const [nextBookings, nextMemberships, nextServices, nextPackages] = await Promise.all([
          getRequesterBookings(db, currentUser.uid),
          getUserActivePackages(db, currentUser.uid, { organizationId: userData.orgId }),
          getOrganizationServices(db, userData.orgId, { activeOnly: true }),
          getOrganizationServicePackages(db, userData.orgId),
        ]);
        setBookings(nextBookings || []);
        setMemberships(nextMemberships || []);
        setServices(nextServices || []);
        setServicePackages(nextPackages || []);
      } finally {
        setLoading(false);
      }
    };

    loadSelection();
  }, [currentUser?.uid, userData?.orgId]);

  const canViewSelection = canViewStudentPackageSelection(userData || {}, {
    currentUserId: currentUser?.uid || '',
    targetUserId: currentUser?.uid || '',
    organizationId: userData?.orgId || '',
  });

  const clientProfile = {
    userId: currentUser?.uid || '',
    displayName: userData?.name || currentUser?.email || 'Student',
    primaryDiscipline: userData?.sport || '',
  };
  const selectedService = services.find((service) => service.serviceId === requestedServiceId) || services[0] || null;
  const selectionView = buildPackageSelectionView(selectedService || {}, {
    servicePackages,
    memberships,
    clientProfile,
  });
  const visiblePackageOptions = selectionView.packages.length > 0
    ? selectionView.packages
    : (servicePackages || []).filter((servicePackage) => servicePackage?.active !== false);
  const recommendedPackage = requestedPackageId
    ? visiblePackageOptions.find((servicePackage) => servicePackage.servicePackageId === requestedPackageId) || null
    : selectionView.recommendedPackage || getRecommendedPackageForClient(clientProfile, {
      memberships,
      servicePackages,
      bookings,
    });
  const bestNextLesson = getBestNextLessonForDisplay(clientProfile, {
    bookings,
    services,
    memberships,
    servicePackages,
  });

  if (!canViewSelection) {
    return (
      <div className="mx-auto max-w-6xl p-6">
        <ProfileHeader />
        <section className="mt-6 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <h1 className="text-2xl font-semibold text-slate-950">Package Selection</h1>
          <p className="mt-3 text-sm text-slate-600">You do not have access to package selection right now.</p>
        </section>
      </div>
    );
  }

  if (loading) {
    return <div className="mx-auto max-w-6xl p-6 text-sm text-slate-600">Loading package options...</div>;
  }

  return (
    <div className="mx-auto max-w-6xl p-6">
      <ProfileHeader />

      <section className="mt-6 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-500">Package Selection</p>
            <h1 className="mt-2 text-2xl font-semibold text-slate-950">Choose the best package for your next step</h1>
            <p className="mt-2 text-sm text-slate-600">
              Compare package value, see what is recommended for you, and keep your next lesson moving forward.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => navigate('/my-bookings')} className="rounded-full bg-slate-900 px-4 py-2 text-sm font-medium text-white">
              Book This Exact Next Service
            </button>
            <Link to="/dashboard/progress" className="rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700">
              Back to Progress
            </Link>
          </div>
        </div>
      </section>

      <section className="mt-6 grid gap-6 lg:grid-cols-2">
        <article className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-950">Best next lesson</h2>
          <div className="mt-4 space-y-2 text-sm text-slate-600">
            <p>{bestNextLesson?.name || selectedService?.name || 'No exact next lesson recommendation yet.'}</p>
            <p>{bestNextLesson?.helperText || 'We will keep this aligned to your recent lessons and feedback.'}</p>
          </div>
        </article>
        <article className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-950">Recommended for you</h2>
          <div className="mt-4 space-y-2 text-sm text-slate-600">
            <p>{recommendedPackage?.name || recommendedPackage?.summary || 'No single package stands out yet.'}</p>
            <p>{recommendedPackage?.savings?.summary || 'You can still compare all available package options below.'}</p>
          </div>
        </article>
      </section>

      <section className="mt-6 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-slate-950">Package comparison</h2>
        {visiblePackageOptions.length === 0 ? (
          <p className="mt-4 text-sm text-slate-600">No active package options are available right now.</p>
        ) : (
          <div className="mt-4 grid gap-4 md:grid-cols-3">
            {visiblePackageOptions.map((servicePackage) => (
              <div key={servicePackage.servicePackageId} className="rounded-2xl border border-slate-200 p-4">
                <div className="flex flex-wrap gap-2">
                  {servicePackage.isRecommended || recommendedPackage?.servicePackageId === servicePackage.servicePackageId ? (
                    <span className="rounded-full bg-sky-50 px-2 py-1 text-xs font-medium text-sky-700">Recommended for you</span>
                  ) : null}
                  {servicePackage.isMostPopular ? (
                    <span className="rounded-full bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-700">Most popular package</span>
                  ) : null}
                  {servicePackage.isBestValue ? (
                    <span className="rounded-full bg-amber-50 px-2 py-1 text-xs font-medium text-amber-700">Best value package</span>
                  ) : null}
                </div>
                <h3 className="mt-3 text-lg font-semibold text-slate-900">{servicePackage.name}</h3>
                <p className="mt-1 text-sm text-slate-600">{servicePackage.description || servicePackage.savings?.summary || 'A practical package option for continued coaching.'}</p>
                <p className="mt-3 text-sm text-slate-600">{servicePackage.price} {servicePackage.currency} · {servicePackage.includedSessionCount || servicePackage.includedCredits || 0} included</p>
                {servicePackage.savings?.summary ? <p className="mt-2 text-xs text-slate-500">{servicePackage.savings.summary}</p> : null}
                <Link
                  to={getPackageSelectionLink(servicePackage, { service: selectedService, organizationId: userData?.orgId || '' })}
                  className="mt-4 inline-flex rounded-xl bg-slate-900 px-3 py-2 text-sm font-medium text-white"
                >
                  Start Package Purchase
                </Link>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

export default StudentPackageSelectionPage;
